/**
 * =============================================================================
 * cart.js
 * =============================================================================
 * Collection of useful utilities for performing actions on the cart / minicart
 * =============================================================================
 */

var Cart = {
  selectors: { 
    line_item: "[data-tile-line-item]",
    line_item_quantity: "[data-cart-item-quantity]",
    line_item_remove: "[data-cart-item-remove]",

    cart_item_count: "[data-cart-item-count]", // Item count badges
    cart_item_count_attribute: "data-cart-item-count",

    minicart: "cart-offcanvas",

    variant_quick_add: "[data-quick-add]"
  },

  minicart: null,

  /**
   * Document ready
   */
  onReady: function(){
    // Handle remove / quick add 'click'
    document.body.addEventListener('click', e => {
      if(e.target.closest(this.selectors.line_item_remove)){
        this.onLineItemRemove(e);
      };

      if(e.target.closest(this.selectors.variant_quick_add)){
        this.onVariantQuickAdd(e);
      };
    });

    // Handle quantity select 'change'
    document.body.addEventListener('change', e => {
      if(e.target.closest(this.selectors.line_item_quantity)){
        this.onLineItemQuantityChange(e);
      };
    });

    this.minicart = new bootstrap.Offcanvas(document.getElementById(this.selectors.minicart));
  },

  /**
   * Add a variant using the data-quick-add attribute as the variant ID
   * 
   * @param {Event} e 
   */
  onVariantQuickAdd: function(e) {
    let quickAddElement = e.target.closest(this.selectors.variant_quick_add);
    let spinner = quickAddElement.querySelector(".spinner-overlay");
    let item = { id: quickAddElement.dataset.quickAdd, quantity: 1 };
    if(spinner) {
      quickAddElement.querySelectorAll("span").forEach(e => {
        e.classList.add("opacity-0")
      });
      spinner.classList.remove("d-none");
    }

    let variantId = quickAddElement.dataset.quickAdd;
    let properties = {};
    if(quickAddElement.dataset.quickAddProperties){
      let propertiesArray = JSON.parse(atob(quickAddElement.dataset.quickAddProperties));
      propertiesArray.forEach(e => {
        let propertyParts = e.split("||||");
        properties[propertyParts[0]] = propertyParts[1];
      });
    } 
    item.properties = properties;

    let sellingPlanId = quickAddElement.dataset?.quickAddSellingPlan || null;
    if(sellingPlanId){
      item.selling_plan = sellingPlanId;
    }

    this.add(item).finally(() => {
      if(spinner) {
        quickAddElement.querySelectorAll("span").forEach(e => {
          e.classList.remove("opacity-0");
        });
        spinner.classList.add("d-none");
      }
    });
  },

  /**
   * Remove a line item from the cart
   * 
   * @param {Event} e 
   */
  onLineItemRemove: function(e) {
    e.preventDefault();
    let lineItem = e.target.closest(this.selectors.line_item);
    let lineItemKey = lineItem.dataset.tileLineItem;
    let payload = { id: lineItemKey, quantity: 0 };
    this.change(payload);
  },

  /**
   * Update quantity of a line item in the cart
   * 
   * @param {Event} e 
   */
  onLineItemQuantityChange: function(e) {
    let lineItem = e.target.closest(this.selectors.line_item);
    let lineItemKey = lineItem.dataset.tileLineItem;
    let payload = { id: lineItemKey, quantity: e.target.value };
    this.change(payload);
  },

  /**
   * Fetch the minicart HTML and update
   */
  fetchAndRenderMinicart: function(show = true) {
    // Get the template-namespaced ID of the cart-items section to dynamically render
    const sectionId = document.querySelector(".cart-offcanvas").id.replace("shopify-section-", "");
    const fetchPromise = fetch(`${location.pathname}?section_id=${sectionId}`)
      .then(response => response.text())
      .then(response => {
        document.querySelector('.cart-offcanvas').outerHTML = response;
        const parsedHtml = new DOMParser().parseFromString(response, 'text/html');
        const numItems = parseInt(parsedHtml.querySelector("#cart-offcanvas-content").dataset.cartItemsCount);
        let itemCounts = document.querySelectorAll("[data-cart-item-count]");
        itemCounts.forEach(e => {
          e.dataset.cartItemCount = numItems;
          e.innerHTML = numItems; 
        });
        mp_lazyload_setup();
        
        let cartJSON = JSON.parse(parsedHtml.querySelector("#minicart-json").innerText)
        var event = new CustomEvent("minicart:rendered", { detail: {
          cart: cartJSON
        }});
        document.dispatchEvent(event);

        if(show){
          this.minicart.show();
        }
      })
      .finally(() => document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none')));
  },

  fetchAndRenderCart: function() {
    // Get the template-namespaced ID of the cart-items section to dynamically render
    const sectionId = document.querySelector(".cart").id.replace("shopify-section-", "");
    const fetchPromise = fetch(`${location.pathname}?section_id=${sectionId}`)
      .then(response => response.text())
      .then(response => {
        const parsedHtml = new DOMParser().parseFromString(response, 'text/html');

        // Update any cart count displays (e.g. badge next to icon) with the new amount
        const numItems = parseInt(parsedHtml.querySelector("#cart-container").dataset.cartItemsCount);
        let itemCounts = document.querySelectorAll("[data-cart-item-count]");
        itemCounts.forEach(e => {
          e.dataset.cartItemCount = numItems;
          e.innerHTML = numItems;
        });

        if(numItems > 0){
          // Update any live attributes with their new refreshed values
          document.querySelectorAll("[data-live-attribute^='cart']").forEach(element => {
            let attributeName = element.dataset.liveAttribute;
            let refreshedElement = parsedHtml.querySelector(`[data-live-attribute="${attributeName}"]`);
            if(refreshedElement){
              let refreshedAttributeValue = refreshedElement.dataset[attributeName];
              element.dataset[attributeName] = refreshedAttributeValue;
            }
          });

          // Update any live regions with their new refreshed content
          document.querySelectorAll("[data-live-region='cart']").forEach(element => {
            let refreshedElement = parsedHtml.getElementById(element.id)
            if(refreshedElement){
              element.innerHTML = refreshedElement.innerHTML;
            }
          });
  
          mp_lazyload_setup();
          window.Klarna?.OnsiteMessaging.refresh();
        } else {
          // Update the entire section, as cart is now empty
          document.querySelector("#cart-container").outerHTML = parsedHtml.querySelector("#cart-container").outerHTML;
        }

        // Send an event with the contents of the new cart
        let cartJSON = JSON.parse(parsedHtml.querySelector("#cart-json").innerText)
        var event = new CustomEvent("cart:rendered", { detail: {
          cart: cartJSON
        }});
        document.dispatchEvent(event);
      })
      .finally(() => document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none')));
  },

  /**
   * Update items already in the cart
   * 
   * @param {Object} updates 
   * @returns {Promise}
   */
  change: function(updates) {
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));

    // Setup the fetch config
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(updates);

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_change_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleErrorMessage(response.description);
      } else {
        this.handleCartUpdated(response, window.cartStrings.updated);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Add item(s) to the cart
   * 
   * @param {Object} data 
   * @returns {Promise}
   */
  add: function(data) {
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));
    
    // Setup the fetch request
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Determine the payload
    if(data instanceof FormData){ // Product form is passed as a FormData object, *not* JSON
      config.body = data;
      delete config.headers['Content-Type'];
    } else if (data instanceof Array){ // Simple array of variant objects - i.e [{id: XXX, quantity: 1, properties: {}}]
      config.body = JSON.stringify({ items: data })
    } else { // Single variant object, so we wrap in an array
      config.body = JSON.stringify({ items: [data] })
    }

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_add_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleErrorMessage(response.description);
      } else {
        this.handleCartUpdated(response, window.cartStrings.added);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Clear the cart
   * 
   * @returns {Promise}
   */
  clear: function() {
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));
    
    // Setup the fetch request
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_clear_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleErrorMessage(response.description);
      } else {
        window.disableNextMinicartOpen = true;
        this.handleCartUpdated(response, window.cartStrings.updated);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Set cart attributes
   * 
   * @param {Object} data Key-value pairs of attributes to set
   * @returns {Promise}
   */
  setCartAttributes: function(data) {
    // Setup the fetch request
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Determine the payload
    config.body = JSON.stringify({ attributes: data });

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_update_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleErrorMessage(response.description);
      } else {
        window.disableNextMinicartOpen = true;
        this.handleCartUpdated(response, window.cartStrings.updated);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Clear cart attributes
   * 
   * @param {Array} attributes Array of attribute keys to clear. If empty, clears all attributes
   * @returns {Promise}
   */
  clearCartAttributes: async function(attributes = []) {
    if(attributes.length == 0){
      // Only way to know what attributes to clear is to check what's been set
      const cart = await fetch(`${routes.cart_url}.json`).then(response => response.json());
      attributes = Object.keys(cart.attributes);
    }

    const emptyAttributes = attributes.reduce((acc, curr) => {
      acc[curr] = '';
      return acc;
    }, {});

    return this.setCartAttributes(emptyAttributes);
  },

  /**
   * Handle a cart add response
   * 
   * @param {Object} response The JSON response
   * @returns {Promise} The Promise from renderCarts()
   */
  handleCartUpdated: function(response, successMessage) {
    let event = new CustomEvent("cart:changed", { detail: {
      response
    }});
    document.dispatchEvent(event);
    return this.renderCarts();
  },

  /**
   * Display error on failure
   */
  handleErrorMessage: function(errorMessage) {
    let message = "";
    if (typeof errorMessage === 'object'){
      for (const [key, value] of Object.entries(errorMessage)) {
        message = `${message} ${key} ${value}`;
      }
    } else {
      message = errorMessage;
    }
    MP.Utilities.toast("warning", message, "fal fa-exclamation-triangle");
    window.disableNextMinicartOpen = true;
    return this.renderCarts();
  },

  /**
   * Render the current cart / minicart
   * 
   * @returns {Promise}
   */
  renderCarts: function() {
    let openMinicart = true;
    if(window.disableNextMinicartOpen){
      openMinicart = false;
      delete window.disableNextMinicartOpen;
    }

    // Actually get the cart 
    if(MP.Utilities.isCartPage()){
      this.fetchAndRenderCart();
      this.fetchAndRenderMinicart(false);
    } else {
      this.fetchAndRenderMinicart(openMinicart);
    }
  }
}

export default Cart;